import * as React from "react";
import {
  Alert,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useLanguage } from "./Language";
import { LoginCredentials } from "./LoginDialog";
import { useForm, SubmitHandler } from "react-hook-form";
import { LoginFormInput } from "./LocalLoginForm";
import { SanitizedHTML } from "./SanitizedHTML";

export interface LoginFormChange {
  username: string;
  password: string;
}

export interface ADLoginFormProps {
  loginMessage?: string;
  displayGuardianLogin?: boolean;
  showLoginError: boolean;
  onClickGuardianLogin?: (event: LoginFormInput) => void;
  isKids: boolean;
  onClickClose: React.MouseEventHandler<HTMLButtonElement>;
  onSubmitLogin: (creds: LoginCredentials) => void;
  defaultUsername?: string;
  defaultPassword?: string;
}

export const ADLoginForm: React.FC<ADLoginFormProps> = (props) => {
  const {
    loginMessage,
    displayGuardianLogin = false,
    showLoginError = false,
    isKids = false,
    onClickGuardianLogin,
    onClickClose,
    onSubmitLogin,
  } = props;

  const { translate } = useLanguage();
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormInput>({
    mode: "onBlur",
    defaultValues: {
      barcode: props.defaultUsername,
      password: props.defaultPassword,
      rememberMe: false,
    },
  });

  const onSubmit: SubmitHandler<LoginFormInput> = ({
    barcode,
    password,
    rememberMe,
  }) => {
    const payload: LoginCredentials = {
      username: barcode,
      password,
      localLoginOnly: false,
      rememberMe,
    };

    onSubmitLogin(payload);
  };

  return (
    <React.Fragment>
      <DialogTitle sx={{ display: "flex" }}>
        <Box sx={{ width: "40px", height: "40px" }}></Box>
        <Box sx={{ textAlign: "center", flexGrow: 1 }}>
          {translate("loginDialogTitle")}
        </Box>
        <Box sx={{ width: "40px", height: "40px" }}></Box>
      </DialogTitle>
      <DialogContent sx={{ minWidth: "320px" }}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack
            sx={{
              gap: 2,
              paddingTop: 1,
            }}
          >
            {showLoginError && (
              <Alert severity="error">
                {isKids
                  ? translate("kidsLoginErrorMessage")
                  : translate("loginErrorMessage")}
              </Alert>
            )}
            {loginMessage && (
              <Alert
                severity="info"
                children={<SanitizedHTML html={loginMessage} />}
              />
            )}
            <TextField
              name="username"
              {...register("barcode", { required: true })}
              error={!!errors.barcode}
              helperText={
                errors.barcode ? translate("requiredFieldError") : " "
              }
              required
              autoFocus
              label={translate("username")}
              id="ad-login-form-username"
            ></TextField>
            <TextField
              name="password"
              {...register("password", { required: true })}
              error={!!errors.password}
              helperText={
                errors.password ? translate("requiredFieldError") : " "
              }
              required
              label={translate("password")}
              type="password"
              id="ad-login-form-password"
            ></TextField>

            <Button type="submit" variant="contained">
              {isKids ? translate("kidsLoginButton") : translate("logon")}
            </Button>
            <Button onClick={onClickClose}>
              {isKids ? translate("kidsLoginCloseButton") : translate("cancel")}
            </Button>
            {displayGuardianLogin && (
              <React.Fragment>
                <Divider>
                  <Typography variant="overline">{translate("or")}</Typography>
                </Divider>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const currentValues = getValues();
                    console.log("Current form values:", currentValues);
                    onClickGuardianLogin(currentValues);
                  }}
                >
                  {translate("guardianLogin")}
                </Button>
              </React.Fragment>
            )}
          </Stack>
        </form>
      </DialogContent>
    </React.Fragment>
  );
};
